import PageShell from "@/components/PageShell";
import PageHero from "@/components/PageHero";
import NodeEconomy from "@/components/NodeEconomy";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const REQUIREMENTS = [
    { t: "Light Node", hw: "2 vCPU · 4 GiB · 100 GB", bw: "50 Mbps", stake: "1,000 DCX", reward: "Bandwidth, relay", risk: "Low", profile: "Home / hobbyist" },
    { t: "Standard Node", hw: "4 vCPU · 16 GiB · 500 GB SSD", bw: "200 Mbps", stake: "10,000 DCX", reward: "Compute, routing", risk: "Medium", profile: "Power user, small operator" },
    { t: "GPU Node", hw: "1× A100/H100 · 64 GiB RAM", bw: "500 Mbps", stake: "25,000 DCX", reward: "AI inference & training", risk: "Medium", profile: "GPU farm, lab" },
    { t: "Storage Node", hw: "8 vCPU · 32 GiB · 4 TB NVMe", bw: "500 Mbps", stake: "15,000 DCX", reward: "Storage + PoS", risk: "Medium", profile: "Storage operator" },
    { t: "Validator", hw: "16 vCPU · 64 GiB · 2 TB NVMe", bw: "1 Gbps", stake: "100,000 DCX", reward: "Block rewards, fees", risk: "High (slashing)", profile: "Institutional operator" },
    { t: "Enterprise Verified", hw: "Tier-1 datacenter", bw: "10 Gbps", stake: "Custom", reward: "SLA-backed capacity", risk: "High", profile: "Datacenter / cloud provider" },
];

export default function NodesPage() {
    return (
        <PageShell>
            <PageHero
                eyebrow="Node Operators"
                title="Own the Cloud. Run a Node."
                accent="Earn From Infrastructure."
                sub="Contribute compute, storage, GPU capacity, or validation to DeCloudX and earn from real network usage — settled on-chain in $DCX."
            />

            <NodeEconomy />

            <section className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Node Requirements</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">Choose your tier based on hardware, bandwidth, and risk profile.</h2>
                    <div className="mt-10 surface-strong rounded-2xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full" data-testid="node-requirements-table">
                                <thead>
                                    <tr className="border-b border-line bg-card-muted">
                                        {["Tier","Hardware","Bandwidth","Stake","Rewards","Risk","Profile"].map((h, i) => (
                                            <th key={i} className="text-left p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {REQUIREMENTS.map((r, i) => (
                                        <tr key={i} className="border-b border-line last:border-b-0 hover:bg-card-muted/50 transition-colors">
                                            <td className="p-4 text-sm text-ink font-medium">{r.t}</td>
                                            <td className="p-4 text-xs text-muted-ink font-mono">{r.hw}</td>
                                            <td className="p-4 text-xs text-muted-ink font-mono">{r.bw}</td>
                                            <td className="p-4 text-xs text-brand-blue font-mono">{r.stake}</td>
                                            <td className="p-4 text-xs text-muted-ink">{r.reward}</td>
                                            <td className="p-4 text-xs text-muted-ink">{r.risk}</td>
                                            <td className="p-4 text-xs text-muted-ink">{r.profile}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="mt-12">
                        <Link to="/contact?role=node_operator" className="btn-primary">Apply to Become a Node Operator <ArrowRight size={15} strokeWidth={2} /></Link>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
